import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import MobileAppBanner from '../components/MobileAppBanner';
import { changePassword } from '../services/api';
import cacheService from '../services/cacheService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPencilAlt, 
  faSave, 
  faTimes,
  faHeart,
  faCalendar,
  faKey,
  faEye,
  faEyeSlash,
  faMapMarkerAlt,
  faRoute,
  faHotel,
  faUser,
  faPhone,
  faEnvelope,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import Toast from '../components/Toast';
import ProfileDashboard from '../components/ProfileDashboard';
import AdPageLayout from '../components/AdPageLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 968px) {
    padding: 10px;
    max-width: 100%;
  }
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MainProfileCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #316C6E 0%, #254F51 100%);
  color: white;
  padding: 2.5rem 2rem;
  position: relative;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  margin: 0;
  font-size: 2.2rem;
  font-weight: 600;
`;

const Location = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 1.1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;



const EditButton = styled.button`
  background:rgb(255, 255, 255);
  border: white;
  color: #316C6E;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  
  &:hover {
    background:rgb(228, 228, 228);
    border-color: #254F51;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(49, 108, 110, 0.3);
  }
`;

const CardContent = styled.div`
  padding: 2rem;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuickLink = styled(Link)`
  background: linear-gradient(135deg, #f8fafb 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-decoration: none;
  color: #334155;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  gap: 0.8rem;
  border: 2px solid transparent;

  &:hover {
    background: linear-gradient(135deg, #316C6E 0%, #254F51 100%);
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(49, 108, 110, 0.3);
    border-color: #316C6E;
  }

  svg {
    font-size: 1.8rem;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

const LinkLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin: 0 0 1.5rem 0;
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  
  > div:first-child {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex: 1;
    
    &:after {
      content: '';
      flex: 1;
      height: 2px;
      background: linear-gradient(to right, #316C6E, transparent);
      margin-left: 0.8rem;
    }
  }
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: ${props => props.isEditing ? '2rem' : '0'};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const InfoLabel = styled.span`
  color: #666;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoValue = styled.input`
  font-size: 1rem;
  color: #333;
  border: ${props => props.isEditing ? '2px solid #e9ecef' : 'none'};
  background-color: ${props => props.isEditing ? 'white' : '#f8f9fa'};
  padding: ${props => props.isEditing ? '0.8rem' : '0.8rem'};
  border-radius: 8px;
  width: 100%;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #316C6E;
  }

  &:disabled {
    cursor: default;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 2px solid #f1f3f4;
`;

const Button = styled.button`
  padding: 0.9rem 1.8rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  background-color: ${props => props.cancel ? '#dc3545' : '#316C6E'};
  color: white;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: ${props => props.cancel ? '#c82333' : '#254F51'};
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const PasswordModal = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 450px;
  max-width: 90vw;

  h2 {
    color: #316C6E;
    margin: 0 0 1.5rem 0;
    font-size: 1.5rem;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  padding-right: 3rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #316C6E;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;

  &:hover {
    color: #316C6E;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.8rem;
  background: #f8d7da;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
`;

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].pages.profile;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await cacheService.getProfile();
      setProfile(data.user);
      setEditedProfile(data.user);
    } catch (error) {
      console.error('Error fetching profile:', error);
      showToastMessage('Error loading profile', 'error');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const updatedProfile = await cacheService.updateProfile(editedProfile);
      setProfile(updatedProfile);
      setIsEditing(false);
      showToastMessage('Profile updated successfully', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showToastMessage('Error updating profile', 'error');
    }
  };

  const handleChange = (e) => {
    setEditedProfile({
      ...editedProfile,
      [e.target.name]: e.target.value
    });
  };

  const showToastMessage = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // Check if there are any changes made to the profile (excluding email)
  const hasChanges = () => {
    if (!profile || !editedProfile) return false;
    
    const fieldsToCheck = ['firstName', 'lastName', 'phone', 'address', 'countryOfOrigin'];
    
    return fieldsToCheck.some(field => {
      const originalValue = profile[field] || '';
      const editedValue = editedProfile[field] || '';
      return originalValue !== editedValue;
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError(t.passwordError.mismatch);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t.passwordError.length);
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError(t.passwordError.same);
      return;
    }

    try {
      await changePassword({
        currentPassword,
        newPassword
      });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordModal(false);
      showToastMessage(t.passwordSuccess, 'success');
    } catch (error) {
      setPasswordError(error.response?.data?.error || t.passwordError.generic);
    }
  };

  if (!profile || !editedProfile) return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <div>{t.loading}</div>
        </PageContainer>
      </AdPageLayout>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <ProfileSection>
            <MainProfileCard>
              <ProfileHeader>
                <ProfileInfo>
                  <Avatar>
                    {`${editedProfile.firstName.charAt(0).toUpperCase()}${editedProfile.lastName.charAt(0).toUpperCase()}`}
                  </Avatar>
                  <UserInfo>
                    <UserName>{`${editedProfile.firstName} ${editedProfile.lastName}`}</UserName>
                    <Location>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {editedProfile.countryOfOrigin || t.locationNotSet}
                    </Location>
                  </UserInfo>
                </ProfileInfo>
              </ProfileHeader>

              <CardContent>
                <QuickActions>
                  <QuickLink to="/favorites">
                    <FontAwesomeIcon icon={faHeart} />
                    <LinkLabel>{t.quickLinks.favorites}</LinkLabel>
                  </QuickLink>
                  <QuickLink to="/bookings">
                    <FontAwesomeIcon icon={faCalendar} />
                    <LinkLabel>{t.quickLinks.bookings}</LinkLabel>
                  </QuickLink>
                  <QuickLink as="div" onClick={() => setShowPasswordModal(true)} style={{ cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faKey} />
                    <LinkLabel>{t.quickLinks.changePassword}</LinkLabel>
                  </QuickLink>
                </QuickActions>

                {showPasswordModal && (
                  <ModalOverlay onClick={() => setShowPasswordModal(false)}>
                    <PasswordModal onClick={e => e.stopPropagation()}>
                      <h2>{t.changePassword}</h2>
                      {passwordError && <ErrorMessage>{passwordError}</ErrorMessage>}
                      <form onSubmit={handlePasswordChange}>
                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showCurrentPassword ? "text" : "password"}
                            placeholder={t.currentPassword}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          >
                            <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
                          </PasswordToggle>
                        </PasswordInputWrapper>

                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showNewPassword ? "text" : "password"}
                            placeholder={t.newPassword}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                          </PasswordToggle>
                        </PasswordInputWrapper>

                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t.confirmPassword}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                          </PasswordToggle>
                        </PasswordInputWrapper>

                        <ActionButtons>
                          <Button cancel onClick={() => setShowPasswordModal(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                            {t.cancel}
                          </Button>
                          <Button type="submit">
                            <FontAwesomeIcon icon={faSave} />
                            {t.changePassword}
                          </Button>
                        </ActionButtons>
                      </form>
                    </PasswordModal>
                  </ModalOverlay>
                )}

                <SectionTitle>
                  <div>
                    <FontAwesomeIcon icon={faUser} />
                    {t.personalInformation}
                  </div>
                  {!isEditing && (
                    <EditButton onClick={handleEdit}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                      {t.editProfile}
                    </EditButton>
                  )}
                </SectionTitle>

                <InfoSection isEditing={isEditing}>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faUser} />
                      {t.firstName}
                    </InfoLabel>
                    <InfoValue
                      name="firstName"
                      value={editedProfile.firstName}
                      onChange={handleChange}
                      isEditing={isEditing}
                      disabled={!isEditing}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faUser} />
                      {t.lastName}
                    </InfoLabel>
                    <InfoValue
                      name="lastName"
                      value={editedProfile.lastName}
                      onChange={handleChange}
                      isEditing={isEditing}
                      disabled={!isEditing}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faEnvelope} />
                      {t.email}
                    </InfoLabel>
                    <InfoValue
                      name="email"
                      value={editedProfile.email}
                      onChange={handleChange}
                      isEditing={false}
                      disabled={true}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faPhone} />
                      {t.phone}
                    </InfoLabel>
                    <InfoValue
                      name="phone"
                      value={editedProfile.phone || ''}
                      onChange={handleChange}
                      isEditing={isEditing}
                      disabled={!isEditing}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {t.address}
                    </InfoLabel>
                    <InfoValue
                      name="address"
                      value={editedProfile.address || ''}
                      onChange={handleChange}
                      isEditing={isEditing}
                      disabled={!isEditing}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <FontAwesomeIcon icon={faGlobe} />
                      {t.country}
                    </InfoLabel>
                    <InfoValue
                      name="countryOfOrigin"
                      value={editedProfile.countryOfOrigin || ''}
                      onChange={handleChange}
                      isEditing={isEditing}
                      disabled={!isEditing}
                    />
                  </InfoItem>
                </InfoSection>

                {isEditing && (
                  <ActionButtons>
                    <Button cancel onClick={handleCancel}>
                      <FontAwesomeIcon icon={faTimes} />
                      {t.cancel}
                    </Button>
                    <Button onClick={handleSave} disabled={!hasChanges()}>
                      <FontAwesomeIcon icon={faSave} />
                      {t.saveChanges}
                    </Button>
                  </ActionButtons>
                )}
              </CardContent>
            </MainProfileCard>
          </ProfileSection>
        </PageContainer>
      </AdPageLayout>

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
      <MobileAppBanner />
    </PageWrapper>
  );
};

export default ProfilePage;
